import React, { useRef, useEffect, useState } from 'react';
import './SearchField.css';

const SearchField = ({
  map,
  isLoaded,
  onLocationSelected,
  onMapMove,
  placeholder = "Search for a location...",
  onLanguageMenuToggle,
  isLanguageMenuOpen,
  showGuestIndicator = true
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Add coordinate validation helper function
  const isValidLatLng = (lat, lng) => {
    return lat != null &&
           lng != null &&
           isFinite(lat) &&
           isFinite(lng) &&
           Math.abs(lat) <= 90 &&
           Math.abs(lng) <= 180;
  };

  useEffect(() => {
    if (!isLoaded || !window.google || !inputRef.current) return;

    // Initialize the autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ['(regions)'], // Focus on geographic locations
        fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components']
      }
    );

    // Listen for place selection
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();

      if (!place.geometry || !place.geometry.location) {
        console.log('No geometry found for the selected place');
        return;
      }

      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      // Validate coordinates before using them
      if (!isValidLatLng(location.lat, location.lng)) {
        console.warn('[SearchField] Invalid coordinates detected, skipping map operation:', location);
        return;
      }

      // Move map to selected location
      if (map) {
        try {
          map.panTo(location);

          // Adjust zoom based on place type
          if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
          } else {
            map.setZoom(12); // Default zoom for places without viewport
          }
        } catch (error) {
          console.error('[SearchField] Error moving map to location:', error);
        }
      }

      // Notify parent components
      if (onLocationSelected) {
        onLocationSelected(place, location);
      }

      if (onMapMove) {
        onMapMove(false); // User is no longer centered on their location
      }

      // Collapse the search field after selection
      setIsExpanded(false);
      inputRef.current.blur();
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, map, onLocationSelected, onMapMove]);

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    // Delay collapse to allow for selection
    setTimeout(() => {
      if (inputRef.current && inputRef.current !== document.activeElement) {
        setIsExpanded(false);
      }
    }, 200);
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  return (
    <div className={`search-field-container ${isExpanded ? 'expanded' : ''}`}>
      <div className="search-field-wrapper">
        <div className="search-icon">
          <i className="fas fa-search" aria-hidden="true"></i>
        </div>
        <input
          ref={inputRef}
          type="text"
          className="search-field-input"
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {isExpanded && (
          <button
            className="search-clear-button"
            onClick={handleClear}
            type="button"
            aria-label="Clear search"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
        {/* Language selector button integrated into search field */}
        <button
          className="search-language-button"
          onClick={() => onLanguageMenuToggle()}
          aria-label="Select language"
          style={{
            background: 'transparent',
            border: '1px solid transparent',
            padding: '6px 8px',
            borderRadius: '8px',
            cursor: 'pointer',
            lineHeight: '1',
            color: '#f0f0f0',
            width: '32px',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '17px',
            position: 'relative'
          }}
        >
          ☰
          {showGuestIndicator && (
            <div
              style={{
                position: 'absolute',
                bottom: '5px',
                right: '5px',
                width: '8px',
                height: '8px',
                backgroundColor: 'limegreen',
                borderRadius: '50%',
                zIndex: 1
              }}
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default SearchField;
